import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { eventService } from '@/lib/api/services/event'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Event,
  EventDetail,
  EventFilterParams,
  EventCommentWithReplies,
  EventCommentCreate,
  EventReplyCreate
} from '@/lib/api/types'

// Query keys for caching
export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (params: PaginationParams, filters: EventFilterParams) =>
    [...eventKeys.lists(), params, filters] as const,
  searches: () => [...eventKeys.all, 'search'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...eventKeys.searches(), searchTerm, params] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
  comments: () => [...eventKeys.all, 'comments'] as const,
  eventComments: (eventId: string) => [...eventKeys.comments(), eventId] as const
}

/**
 * Hook to fetch events with pagination and filters
 */
export function useFilterEvents(
  params: PaginationParams = { page: 1, size: 10 },
  filters: EventFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Event>>, Error>({
    queryKey: eventKeys.list(params, filters),
    queryFn: () => eventService.getEvents(params, filters),
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to search events with pagination
 */
export function useSearchEvents(
  searchTerm: string,
  params: PaginationParams = { page: 1, size: 10 }
) {
  return useQuery<ApiResponse<PaginatedResponse<Event>>, Error>({
    queryKey: eventKeys.search(searchTerm, params),
    queryFn: () => eventService.searchEvents(searchTerm, params),
    enabled: searchTerm.trim().length > 0, // Only search if there's a search term
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch a single event by ID
 */
export function useEvent(eventId?: string) {
  return useQuery<ApiResponse<EventDetail>, Error>({
    queryKey: eventKeys.detail(eventId || ''),
    queryFn: () => eventService.getEventById(eventId!),
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

/**
 * Hook to fetch event comments with replies
 */
export function useEventComments(eventId?: string) {
  return useQuery<ApiResponse<Array<{ eventReplyResponseDetails: EventCommentWithReplies[] }>> , Error>({
    queryKey: eventKeys.eventComments(eventId || ''),
    queryFn: () => eventService.getEventCommentsWithReplies(eventId!),
    enabled: !!eventId,
    staleTime: 1 * 60 * 1000 // 1 minute
  })
}

/**
 * Hook to add a comment to an event
 */
export function useAddEventComment(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (commentData: EventCommentCreate) => 
      eventService.addEventComment(eventId, commentData),
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: eventKeys.eventComments(eventId) })
    }
  })
}

/**
 * Hook to add a reply to a comment
 */
export function useAddEventReply(eventId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, replyData }: { commentId: string; replyData: EventReplyCreate }) => 
      eventService.addEventReply(commentId, replyData),
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: eventKeys.eventComments(eventId) })
    }
  })
}

